import { 
  Divider, 
  EmptyPlaceholder,
  ImageRadioGroup,
  SearchInput,
} from "@components"

import { 
  Button, 
  Card,
  CardBody,
  CardHeader,
  Checkbox,  
  Select,
  SelectItem 
} from "@heroui/react"

import PropTypes from 'prop-types'

const AvailabilitySelector = ({
  availabilityType,
  availabilityTypes,
  endTime,
  groups,
  handleClearAll,
  handleAvailabilityTypeChange,
  handleEndTimeChange,
  handleSearchChange, 
  handleSearchClear,
  handleSelectAll,
  handleStartTimeChange,
  handleToggleSelected,
  noOfDeselected,
  noOfSelected,
  searchTerm,
  selectedGroupIds = [],
  slots,
  startTime,
}) => {
  const slotTimes = slots.map(slot => slot.starts_at.format("HH:mm"))
  const availableSlotEndTimes = startTime ? slotTimes.filter((time) => time >= startTime) : slotTimes

  const noOfSlotsAffected = startTime && endTime 
    ? slotTimes.filter((time) => time >= startTime && time <= endTime).length
    : 0

  const noOfGroupsAffected = selectedGroupIds.length

  return (
    <div>
      <div>
        <div className="my-2">
          <p className="font-semibold text-content2-foreground">
            1) Select slot range
            <span className="ml-2 text-sm text-danger">(Bookings will not be affected)</span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Select onSelectionChange={(time) => handleStartTimeChange(time.currentKey)} selectedKeys={startTime ? [startTime] : []} aria-label="Start Time">
            {slotTimes.map((time) => (
              <SelectItem key={time}>{time}</SelectItem>
            ))}
          </Select>

          <Select onSelectionChange={(time) => handleEndTimeChange(time.currentKey)} selectedKeys={endTime ? [endTime] : []} aria-label="End Time">
            {availableSlotEndTimes.map((time) => (
              <SelectItem key={time}>{time}</SelectItem>
            ))}
          </Select>
        </div>
      </div>

      <Divider className="my-3" />

      <div>
        <p className="mb-2 font-semibold text text-content2-foreground">2) Select new status</p>
        
        <ImageRadioGroup 
          classNames={{ items: "min-w-52 !p-2 !border-5" }} 
          options={availabilityTypes} 
          handleChange={(option) => { handleAvailabilityTypeChange(option) }} 
          selectedOptionIds={[availabilityType.id]} 
          isDisabled={false}
          multiSelect={false}
          flexDirection="row"
        />
      </div>

      <Divider className="my-3" />
      
      <div>
        <p className="mb-2 font-semibold text-content2-foreground">3) Select Groups</p>

        <div className="flex items-center gap-2">
          <SearchInput
            placeholder="Search by Teacher"
            handleChange={handleSearchChange}
            handleClear={handleSearchClear}
            value={searchTerm}
          />

          <Button className="shrink-0" onPress={() => handleSelectAll()}>Select All ({noOfDeselected})</Button>
          <Button className="shrink-0" onPress={() => handleClearAll()}>Clear All ({noOfSelected})</Button>
        </div>

        {groups.length === 0 && (
          <EmptyPlaceholder 
            classNames="mt-3" 
            title="No groups found" 
            description="There are no groups available. Adjust your search." 
          />
        )}

        {groups.length > 0 && (
          <div id="group-selector" className="overflow-y-scroll h-[27vh] mt-4 bg-content2 p-4 rounded-lg">
              {groups.map((group) => {
                return (
                  <div key={group.id}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Checkbox 
                          className="mr-1" 
                          size="md" 
                          isSelected={selectedGroupIds.includes(group.id)} 
                          onValueChange={(e) => handleToggleSelected(group.id)} 
                        />
                        
                      <p className="text-sm">{group.title}</p>
                      </div>
                    </div>
                    
                    <Divider className="my-2" />
                  </div>
                )
              })}
          </div>
        )}
      </div>

      <Card className="mt-4 bg-content2">
        <CardHeader className="-mb-4 font-semibold">Preview</CardHeader>
        <CardBody className="px-4">
          <p>
            {noOfSlotsAffected} {noOfSlotsAffected === 1 ? "slot" : "slots"} across {noOfGroupsAffected} {noOfGroupsAffected === 1 ? "group" : "groups"} will be marked as <span className={`font-semibold ${availabilityType.code === "AVAILABLE" ? "text-green-500" : "text-red-500"}`}>{availabilityType.label}</span>
          </p>
        </CardBody>
      </Card>
    </div>
  )
}

AvailabilitySelector.propTypes = {
  availabilityType: PropTypes.object.isRequired,
  availabilityTypes: PropTypes.object.isRequired,
  endTime: PropTypes.string,
  groups: PropTypes.array.isRequired,
  handleClearAll: PropTypes.func.isRequired,
  handleAvailabilityTypeChange: PropTypes.func.isRequired,
  handleEndTimeChange: PropTypes.func.isRequired,
  handleSearchChange: PropTypes.func.isRequired,
  handleSearchClear: PropTypes.func.isRequired,
  handleSelectAll: PropTypes.func.isRequired,
  handleStartTimeChange: PropTypes.func.isRequired,
  handleToggleSelected: PropTypes.func.isRequired,
  noOfDeselected: PropTypes.number.isRequired,
  noOfSelected: PropTypes.number.isRequired,
  searchTerm: PropTypes.string.isRequired,
  selectedGroupIds: PropTypes.array,
  slots: PropTypes.array.isRequired,
  startTime: PropTypes.string,
}

export default AvailabilitySelector